import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';

import request from 'lib/request';
import type { TractNriMatrixResponse } from 'types/api-responses';

/** Cache key for the tract NRI matrix atomFamily. */
export interface TractNriMatrixKey {
  tractId: string;
  /** For LTD slots only - use slot_id query param */
  slotId?: string;
  /** For non-LTD slots (actual wells) - use well_uwi query param */
  wellUwi?: string;
  /** On a package page: only the package's own Subtracts of the Tract (package_id). */
  packageId?: number;
}

/**
 * Stable string key for atomFamily cache comparison.
 *
 * @param {TractNriMatrixKey} params - The tract NRI matrix key parameters.
 * @return {string} A string combining tractId, slotId, wellUwi and packageId.
 */
const createTractNriMatrixKey = (params: TractNriMatrixKey): string =>
  `${params.tractId}-${params.slotId ?? ''}-${params.wellUwi ?? ''}-${
    params.packageId ?? ''
  }`;

/**
 * Lazy-fetches the aggregated tract NRI matrix. Each unique
 * `(tractId, slotId | wellUwi)` triple gets its own atom and cache slot, so
 * multiple `TractNriMatrixCard`s can render simultaneously without conflict.
 *
 * Endpoint: `GET /nri_calculations/tract_matrix?tract_id=…&(slot_id|well_uwi)=…[&package_id=…]`.
 */
export const tractNriMatrixAtomFamily = atomFamily(
  (params: TractNriMatrixKey) =>
    loadable(
      atom(async (): Promise<TractNriMatrixResponse> => {
        const { tractId, slotId, wellUwi, packageId } = params;

        const queryParams = new URLSearchParams();
        queryParams.append('tract_id', tractId);

        if (slotId) {
          queryParams.append('slot_id', slotId);
        } else if (wellUwi) {
          queryParams.append('well_uwi', wellUwi);
        } else {
          throw new Error('Either slotId or wellUwi must be provided');
        }
        if (packageId !== undefined) {
          queryParams.append('package_id', String(packageId));
        }

        const response = await request.get<{ data: TractNriMatrixResponse }>(
          `/nri_calculations/tract_matrix?${queryParams.toString()}`
        );

        if ('error' in response) {
          throw new Error((response as { error: string }).error);
        }

        return response.data;
      })
    ),
  // Equality function for cache key comparison
  (a, b) => createTractNriMatrixKey(a) === createTractNriMatrixKey(b)
);
